/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Form, Input, Row, Select, Skeleton, Typography } from 'antd';
import BotAvatar from '../UI/BotAvatar';

// Misc imports
import { NAV_BOT } from '../../constants/navigation';
import { useCreateActivityMutation } from '../../store/activities/activitiesApi';
import is from '../../utils/is';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { cloneFlowToTargetLanguage } from '../../utils/transform/cloneFlowContentToTargetLanguage';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Option } = Select;
const { Text } = Typography;

const ActivityCloneForm = ({
  activityToClone,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { primaryBotLanguage } = useBotLanguage();

  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId });
  const [createActivity, { isLoading: isCreatingActivity }] = useCreateActivityMutation({ fixedCacheKey: 'create-activity-shared' });

  const [showConflictMessage, setShowConflictMessage] = useState(false);

  useEffect(() => {
    setShowConflictMessage(false);
    // Reset data on activity change
    return () => form.resetFields();
  }, [activityToClone, activityToClone?.name?.[primaryBotLanguage]]);

  const onSubmit = async (values) => {
    let newActivity = cloneFlowToTargetLanguage(bots, tenantId, values, activityToClone, true);
    const targetBotPrimaryLanguage = bots.items.find((bot) => bot.id === values.tenantId)?.settings.languages.primary;
    const saveResult = await createActivity({ orgId, tenantId: values.tenantId, data: newActivity, errorMessage: { showMessage: false }, successMessage: { showMessage: false } });

    if (saveResult && !saveResult.error) {
      openConsoleAlertMessage({
        type: 'success',
        message: t('Flow cloned'),
        duration: 10,
        secondaryButton: {
          action: () => {
            navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: values.tenantId })}?view=behavior&tab=flows&contentLang=${targetBotPrimaryLanguage}&activity=${newActivity.id}`);
          },
          title: t('View')
        }
      });
      // Trigger callback to notify parent component of success
      onSuccess(newActivity);
    } else {
      openConsoleAlertMessage({ type: 'error', message: t('There was an error cloning the flow.') });
    }
  };

  const checkForLanguageConflicts = (botId) => {
    const selectedBot = bots.items?.find(bot => bot.id === botId);
    setShowConflictMessage(selectedBot?.settings?.languages?.primary !== primaryBotLanguage);
  };

  return (
    <Row gutter={24}>
      <Col xs={24}>
        <Form
          wrapperCol={{ span: 24 }}
          name="activityCloneForm"
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            tenantId: tenantId,
            newName: `${activityToClone.name[primaryBotLanguage]} (Copy)`
          }}
          form={form}
          requiredMark={false}
        >
          <Row gutter={16} className="mb-3">
            <Col
              xs={24}
              className={classNames({ 'mb-3': showConflictMessage })}
            >
              <Form.Item
                name="tenantId"
                label={
                  <Row>
                    <Col span={24}>
                      <Text>
                        {t('Agent')}
                      </Text>
                    </Col>
                    <Col span={24} className="mb-1">
                      <Text type="secondary">
                        {t('The cloned flow will be added to the agent you choose')}.
                      </Text>
                    </Col>
                  </Row>
                }
                rules={[{
                  required: true,
                  message: `${t('The cloned flow will be added to the agent you choose')}.`
                }]}
                validateStatus={showConflictMessage ? 'warning' : undefined}
                help={showConflictMessage
                  ? t('This agent has a different primary language than the current agent, which may lead to inconsistencies or issues in certain parts of the content.')
                  : undefined
                }
              >
                <Select
                  placeholder={t('Choose an agent')}
                  size="large"
                  disabled={isFetchingBots}
                  showSearch
                  className="mb-1"
                  onChange={checkForLanguageConflicts}
                  filterOption={(input, option) =>
                    bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {
                    // The dataset is all bots, minus the bots that were added already
                    bots && is.array(bots.items) && [...bots.items].sort((a, b) => a.displayName.localeCompare(b.displayName)).map(bot => (
                      <Option key={bot.id} value={bot.id}>
                        {
                          isFetchingBots ? (
                            <Skeleton active title={{ width: '80%' }} paragraph={false} className="mt-3 border-radius-base" />
                          ) : (
                            <BotAvatar bot={bot} avatarSize={24} />
                          )
                        }
                      </Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="newName"
                label={
                  <Row>
                    <Col span={24}>
                      <Text>
                        {t('Name')}
                      </Text>
                    </Col>
                    <Col span={24} className="mb-1">
                      <Text type="secondary">
                        {t('The cloned flow name')}.
                      </Text>
                    </Col>
                  </Row>
                }
              >
                <Input
                  size="large"
                  autoFocus={true}
                  onFocus={(e) => e.target.select()}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isCreatingActivity}
            >
              {t('Clone flow')}
            </Button>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

ActivityCloneForm.propTypes = {
  activityToClone: PropTypes.object,
  onSuccess: PropTypes.func
};

export default ActivityCloneForm;
